import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ClientEndpoints, EventTriggers, Facilities } from "@app/shared/src/api/api.gen";
import { ClientEndpoint, CollisionSensitivity, Device, EventTrigger, Facility, FleetDefaults } from "@app/shared/src/models";
import { Card, CardNote, CardHead, FRow, MPS_TO_MPH, Note, Slider, Spinner, Tag, TextInput, useToast } from "@app/shared/src/components_v2";
import { PassSide } from "@app/shared/src/helpers/zoneNameHelpers";
import { MAX_SIDE_PASS_LANES, MIN_SIDE_PASS_LANES } from "@app/shared/src/helpers/sidePassGeometry";
import { effectiveRelayWiring } from "@app/shared/src/helpers/relayWiring";
import { findRelayEndpoint, hasBlankRelayExpressions } from "../../helpers/notificationSettings";
import {
    AlertSettings,
    SENSITIVITY_FIELD,
    alertSettingsFromDefaults,
    alertSettingsMatch,
    describeAlertDifferences,
    findCollisionTrigger,
    findSidePassTrigger,
    findSpeedingTrigger,
    isSpeedingLimitMisplaced,
    triggersForFacility,
    readAlertSettings,
    setRelayPulseSeconds,
} from "../../helpers/alertSettings";
import {
    PROTECTED_ZONE_STEP,
    ensureCollisionTrigger,
    ensureProtectedZone,
    ensureSidePassTrigger,
    ensureSidePassZone,
    ensureSpeedingTrigger,
    findProtectedZone,
    isTargetFilterMissing,
    pinToDevice,
    repairTrigger,
} from "../../helpers/alertProvisioning";
import { InheritedSection } from "../shared/InheritedSection";
import { ProgressDialog, useProgressSteps } from "../shared/ProgressDialog";
import { SaveBar } from "../shared/SaveBar";
import { Toggle } from "../shared/Toggle";

/**
 * Alerts and relays for one truck.
 *
 * The section that makes "a device's settings" a useful fiction: the collision thresholds are
 * columns on a trigger, the relay pulses are a JSON blob on a trigger-endpoint join, and side-pass
 * width is zone geometry. Saving fans out across all three, and enabling a side pass provisions the
 * zone and trigger when the truck was commissioned without them — a settings screen that could only
 * edit what already exists would leave exactly the units that need standardising unfixable.
 *
 * Speeds are shown in mph and stored in m/s, so the draft holds display strings and converts only on
 * save; a half-typed number is not something to push to a truck.
 */

const SENSITIVITY_STOPS = [
    { key: String(CollisionSensitivity.Lowest), label: "Lowest" },
    { key: String(CollisionSensitivity.Low), label: "Low" },
    { key: String(CollisionSensitivity.Medium), label: "Medium" },
    { key: String(CollisionSensitivity.High), label: "High" },
    { key: String(CollisionSensitivity.Highest), label: "Highest" },
];

const mph = (mps: number) => String(Math.round(mps * MPS_TO_MPH));
const secs = (s: number) => s.toFixed(1);

/** The draft, in the units on screen. Parsed back to m/s and seconds only when saving. */
type Draft = {
    approachMph: string;
    reactionSeconds: string;
    sensitivity: CollisionSensitivity;
    lightsSeconds: string;
    hornSeconds: string;
    speedingEnabled: boolean;
    speedingMph: string;
    leftPassEnabled: boolean;
    rightPassEnabled: boolean;
    sidePassMph: string;
    leftPassLanes: number;
    rightPassLanes: number;
};

const toDraft = (s: AlertSettings): Draft => ({
    approachMph: mph(s.minApproachSpeedMps),
    reactionSeconds: secs(s.reactionTimeSeconds),
    sensitivity: s.sensitivity,
    lightsSeconds: secs(s.lightsPulseSeconds),
    hornSeconds: secs(s.hornPulseSeconds),
    speedingEnabled: s.speedingEnabled,
    speedingMph: mph(s.speedingThresholdMps),
    leftPassEnabled: s.leftPassEnabled,
    rightPassEnabled: s.rightPassEnabled,
    sidePassMph: mph(s.sidePassMinSpeedMps),
    leftPassLanes: s.leftPassLanes,
    rightPassLanes: s.rightPassLanes,
});

const fromDraft = (d: Draft): AlertSettings => ({
    minApproachSpeedMps: Number(d.approachMph) / MPS_TO_MPH,
    reactionTimeSeconds: Number(d.reactionSeconds),
    sensitivity: d.sensitivity,
    lightsPulseSeconds: Number(d.lightsSeconds),
    hornPulseSeconds: Number(d.hornSeconds),
    speedingEnabled: d.speedingEnabled,
    speedingThresholdMps: Number(d.speedingMph) / MPS_TO_MPH,
    leftPassEnabled: d.leftPassEnabled,
    rightPassEnabled: d.rightPassEnabled,
    sidePassMinSpeedMps: Number(d.sidePassMph) / MPS_TO_MPH,
    leftPassLanes: d.leftPassLanes,
    rightPassLanes: d.rightPassLanes,
});

const positive = (v: string) => Number.isFinite(Number(v)) && Number(v) > 0;

export const AlertsRelaysSection: React.FC<{
    device: Device;
    triggers?: EventTrigger[];
    defaults?: FleetDefaults;
    onSaved: () => void;
}> = ({ device, triggers, defaults, onSaved }) => {
    const toast = useToast();
    const [facility, setFacility] = useState<Facility>();
    const [relayEndpoint, setRelayEndpoint] = useState<ClientEndpoint>();
    const [busy, setBusy] = useState(false);
    const [overridden, setOverridden] = useState(false);
    // The save's progress bar — see ProgressDialog.
    const { run: progressRun, begin: beginProgress, report: reportProgress, fail: failProgress, end: endProgress } =
        useProgressSteps();

    // Needed to provision zones: updateLayoutConfiguration replaces a facility's whole shape set, so
    // the current one has to be in hand before anything is written back. The relay endpoint rides
    // along because it carries this truck's wiring overrides — which terminal is the horn HERE.
    useEffect(() => {
        let cancelled = false;
        if (!device.facilityId) return;
        Facilities.getById(device.facilityId)
            .then((f) => !cancelled && setFacility(f))
            .catch(() => undefined);
        ClientEndpoints.getAll(device.organizationId, device.facilityId)
            // Same selection rule as the device page's Relay health row, so the relay whose wiring is
            // edited here is the relay whose health is reported there.
            .then((e) => !cancelled && setRelayEndpoint(findRelayEndpoint(e, device.facilityId)))
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [device.organizationId, device.facilityId]);

    // This truck's effective wiring: its endpoint's overrides over the fleet default.
    const wiring = useMemo(() => effectiveRelayWiring(defaults, relayEndpoint), [defaults, relayEndpoint]);

    const saved = useMemo(
        () => (defaults && triggers ? readAlertSettings(triggers, defaults, wiring) : undefined),
        [triggers, defaults, wiring],
    );
    const [draft, setDraft] = useState<Draft>();
    useEffect(() => setDraft(saved ? toDraft(saved) : undefined), [saved]);

    const fleet = defaults ? alertSettingsFromDefaults(defaults) : undefined;

    /*
     * No collision trigger governs this truck at all — not its own, not a fleet-wide one. This is
     * invisible to the settings comparison, because readAlertSettings stands the fleet's values in
     * for an absent trigger: a truck that was never set up reads exactly like one following the
     * default, and the veil would say so over a unit that cannot warn anyone. So it is surfaced as
     * its own state, and it holds the section out of "inherited" so the reset — whose commit
     * provisions the zone and trigger — is actually offered.
     */
    const unprotected = Boolean(triggers && !findCollisionTrigger(triggers));

    /*
     * This truck's own speeding trigger carries its limit in MinValue with no MaxValue — the shape
     * an earlier console build wrote. The device reads that as a floor and fires on every vehicle
     * SLOWER than the limit, which is exactly backwards. It reads back as the number the director
     * typed, so the settings comparison cannot see it; surfaced as its own state, and it holds the
     * section out of "inherited" so a save (which writes the limit to MaxValue) is reachable.
     */
    const speedingRepair = Boolean(
        triggers && isSpeedingLimitMisplaced(findSpeedingTrigger(triggersForFacility(triggers, device.facilityId))),
    );

    /*
     * This truck's own triggers were written without a target filter (the device matches no target
     * type at all) or with relay entries whose expression is blank (the device never evaluates
     * them) — the shape earlier builds of this console and the wizard left behind. Either way the
     * alert exists on paper and fires nothing, and neither is visible in the values a director
     * reads back. Surfaced like the speeding repair: any save, or the reset, writes both fixes.
     */
    const firingRepair = Boolean(
        triggers &&
            triggersForFacility(triggers, device.facilityId).some(
                (t) => isTargetFilterMissing(t) || hasBlankRelayExpressions(t),
            ),
    );

    const inherited =
        saved && fleet
            ? alertSettingsMatch(saved, fleet) && !overridden && !unprotected && !speedingRepair && !firingRepair
            : false;
    // Both values, not just the field name — see describeAlertDifferences.
    const differences = saved && fleet ? describeAlertDifferences(saved, fleet) : [];

    const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => (d ? { ...d, [key]: value } : d));

    const edited = Boolean(draft && saved && JSON.stringify(draft) !== JSON.stringify(toDraft(saved)));
    // A misplaced speeding limit or a trigger that cannot fire is repaired by any save, so the bar
    // is offered even with no edits.
    const dirty = edited || (Boolean(draft && saved) && (speedingRepair || firingRepair));
    const valid = Boolean(
        draft &&
            positive(draft.approachMph) &&
            positive(draft.reactionSeconds) &&
            positive(draft.lightsSeconds) &&
            positive(draft.hornSeconds) &&
            positive(draft.speedingMph) &&
            positive(draft.sidePassMph),
    );

    /**
     * Write one side's zone and trigger, creating either if the truck never had them.
     *
     * The outcome says what became of it, and carries a note when it was not simply done: a side
     * zone's length is measured off the protected zone, so a truck without one cannot have side
     * passes until its layout is set, and saying so beats a toggle that flips and silently does
     * nothing. "noop" is the side that is off and was never set up — nothing to write, nothing to
     * report, and the planner leaves it off the progress bar for the same reason.
     */
    type SidePassOutcome = { state: "done" | "skipped" | "failed"; note?: string } | { state: "noop" };

    const applySidePass = useCallback(
        async (side: PassSide, enabled: boolean, lanes: number, minSpeedMps: number): Promise<SidePassOutcome> => {
            const own = triggersForFacility(triggers ?? [], device.facilityId);
            const existing = findSidePassTrigger(own, side);

            // Governed by an org-wide trigger this truck does not own. Editing it would repoint
            // another truck's alert at this zone, and a second would double up.
            if (!existing && triggers && findSidePassTrigger(triggers, side)) {
                return {
                    state: "skipped",
                    note: `${device.name}'s ${side}-side pass is a fleet-wide alert, so it was left alone. Edit it under Integrations.`,
                };
            }

            // Nothing to provision when it is already off and was never set up.
            if (!enabled && !existing) return { state: "noop" };

            if (!facility) {
                return { state: "failed", note: `Could not load ${device.name}'s site, so its ${side}-side pass was left alone.` };
            }

            const zoned = await ensureSidePassZone(facility, side, lanes);
            if (!zoned.ok) {
                return {
                    state: "failed",
                    note: `${device.name} has no protected zone yet, so its ${side}-side pass could not be sized. Set the unit's zone on the main dashboard first.`,
                };
            }
            setFacility(zoned.facility);

            await ensureSidePassTrigger({
                existing,
                zoneId: zoned.zone.id!,
                // Bound to this truck's site. An unbound trigger governs every facility in the org,
                // so a side pass drawn over one truck's zone would apply to the whole fleet.
                facilityId: device.facilityId,
                organizationId: device.organizationId,
                minSpeedMps,
                reactionTimeSeconds: defaults?.driverReactionTimeSeconds,
                enabled,
            });
            return { state: "done" };
        },
        [facility, triggers, defaults, device.name, device.facilityId, device.organizationId],
    );

    const commit = async (next: AlertSettings, message: string, title: string) => {
        if (!triggers) return;
        setBusy(true);
        const notes: string[] = [];
        try {
            // Only this truck's own triggers are writable — see triggersForFacility.
            const own = triggersForFacility(triggers, device.facilityId);

            const collision = findCollisionTrigger(own);
            const collisionInherited = !collision && Boolean(findCollisionTrigger(triggers));
            const speeding = findSpeedingTrigger(own);
            const speedingInherited = !speeding && Boolean(findSpeedingTrigger(triggers));

            /*
             * The plan, decided before the first write so the progress bar has a length. A side
             * that is off and was never set up is not a step at all — nothing happens to it, and
             * applySidePass reports the same "noop" — while an inherited fleet-wide alert is a step
             * that will settle as "left alone" rather than vanish from the list.
             */
            const stepFor = (key: string, name: string, existing: boolean, inherited: boolean) => ({
                key,
                label: existing ? `Saving ${name}` : inherited ? `Checking ${name}` : `Setting up ${name}`,
            });
            const sideStep = (side: PassSide, enabled: boolean) => {
                const existing = Boolean(findSidePassTrigger(own, side));
                const inherited = !existing && Boolean(findSidePassTrigger(triggers, side));
                if (!existing && !inherited && !enabled) return [];
                return [stepFor(side, `${side}-side pass`, existing, inherited)];
            };
            const drawingZone = !collision && !collisionInherited && facility !== undefined && !findProtectedZone(facility);
            const COLLISION_STEP = stepFor("collision", "impending collision", Boolean(collision), collisionInherited);
            const SPEEDING_STEP = stepFor("speeding", "speeding", Boolean(speeding), speedingInherited);

            beginProgress(title, [
                ...(drawingZone ? [PROTECTED_ZONE_STEP] : []),
                COLLISION_STEP,
                SPEEDING_STEP,
                ...sideStep("left", next.leftPassEnabled),
                ...sideStep("right", next.rightPassEnabled),
            ]);

            reportProgress({ ...COLLISION_STEP, state: "active" });
            if (collision) {
                collision.additionalValue = next.minApproachSpeedMps;
                collision.minValue = next.reactionTimeSeconds;
                // Sensitivity rides MaxValue on this trigger — free here (no speed ceiling on an
                // impending trigger), and it is where the firmware reads it from.
                collision[SENSITIVITY_FIELD] = next.sensitivity;
                setRelayPulseSeconds(collision, wiring.lights.number, next.lightsPulseSeconds);
                setRelayPulseSeconds(collision, wiring.horn.number, next.hornPulseSeconds);
                pinToDevice(collision);
                repairTrigger(collision);
                await EventTriggers.update(collision);
                reportProgress({ ...COLLISION_STEP, state: "done" });
            } else if (collisionInherited) {
                // Already protected by a fleet-wide trigger. Editing it would retune every truck it
                // governs; a second would fire twice on the same vehicle.
                const note = `${device.name} is protected by a fleet-wide collision alert, so it was left alone. Edit it under Integrations.`;
                notes.push(note);
                reportProgress({ ...COLLISION_STEP, state: "skipped", note });
            } else if (facility && defaults) {
                // Missing entirely: build it rather than report it. A truck with no collision
                // trigger is not protected, which is the one state this console must not leave be.
                // A zone is normally captured when the device is added; if this one arrived without,
                // it gets the fleet's stated TMA footprint rather than a guess.
                if (drawingZone) reportProgress({ ...PROTECTED_ZONE_STEP, state: "active" });
                const zoned = await ensureProtectedZone(facility, defaults.tmaWidthMetres, defaults.tmaLengthMetres);
                if (zoned) {
                    if (drawingZone) reportProgress({ ...PROTECTED_ZONE_STEP, state: "done" });
                    setFacility(zoned.facility);

                    const ensured = await ensureCollisionTrigger({
                        facility: zoned.facility,
                        organizationId: device.organizationId,
                        minApproachSpeedMps: next.minApproachSpeedMps,
                        reactionTimeSeconds: next.reactionTimeSeconds,
                        sensitivity: next.sensitivity,
                    });
                    if ("blocked" in ensured) {
                        const note = `${device.name}'s collision alert could not be created because its protected zone is missing.`;
                        notes.push(note);
                        reportProgress({ ...COLLISION_STEP, state: "failed", note });
                    } else {
                        // Named even on success: "back on the fleet default" undersells what just
                        // happened to a truck that had no protection at all.
                        if (ensured.created) {
                            notes.push(
                                zoned.created
                                    ? `Created ${device.name}'s protected zone (at the fleet's TMA size) and its collision alert.`
                                    : `Created ${device.name}'s collision alert over its protected zone.`,
                            );
                        }
                        reportProgress({ ...COLLISION_STEP, state: "done" });
                    }
                } else {
                    // Failing to draw the zone must not fail silently: the save would report
                    // success while leaving the truck exactly as unprotected as it arrived.
                    const note = `${device.name}'s protected zone could not be created, so its collision alert is still missing.`;
                    notes.push(note);
                    if (drawingZone) reportProgress({ ...PROTECTED_ZONE_STEP, state: "failed", note });
                    reportProgress({ ...COLLISION_STEP, state: "failed", note });
                }
            } else {
                const note = `Could not load ${device.name}'s site, so its collision alert could not be created — try again.`;
                notes.push(note);
                reportProgress({ ...COLLISION_STEP, state: "failed", note });
            }

            reportProgress({ ...SPEEDING_STEP, state: "active" });
            if (speedingInherited) {
                const note = `${device.name}'s speeding alert is fleet-wide, so it was left alone. Edit it under Integrations.`;
                notes.push(note);
                reportProgress({ ...SPEEDING_STEP, state: "skipped", note });
            } else {
                await ensureSpeedingTrigger({
                    existing: speeding,
                    facilityId: device.facilityId,
                    organizationId: device.organizationId,
                    thresholdMps: next.speedingThresholdMps,
                    enabled: next.speedingEnabled,
                });
                reportProgress({ ...SPEEDING_STEP, state: "done" });
            }

            const sides: { side: PassSide; enabled: boolean; lanes: number }[] = [
                { side: "left", enabled: next.leftPassEnabled, lanes: next.leftPassLanes },
                { side: "right", enabled: next.rightPassEnabled, lanes: next.rightPassLanes },
            ];
            for (const { side, enabled, lanes } of sides) {
                // Planned and performed from the same inputs, so a side with a step is exactly a
                // side applySidePass will not answer "noop" for.
                const step = sideStep(side, enabled)[0];
                if (step) reportProgress({ ...step, state: "active" });
                const outcome = await applySidePass(side, enabled, lanes, next.sidePassMinSpeedMps);
                if (outcome.state === "noop") continue;
                if (outcome.note) notes.push(outcome.note);
                if (step) reportProgress({ ...step, state: outcome.state, note: outcome.note });
            }

            setOverridden(false);
            endProgress();
            toast(notes.length > 0 ? notes[0] : message);
            onSaved();
        } catch (err) {
            // The dialog stays up with the broken step marked — see ProgressDialog.
            failProgress(err instanceof Error ? err.message : "Could not save that");
        } finally {
            setBusy(false);
        }
    };

    if (!draft || !saved) {
        return (
            <Card>
                <div className="grid place-items-center p-12 text-[var(--muted)]">
                    <Spinner />
                </div>
            </Card>
        );
    }

    const laneRow = (side: PassSide, enabled: boolean, lanes: number) => (
        <FRow
            title={`${side === "left" ? "Left" : "Right"}-side pass`}
            sub={side === "left" ? "Traffic side of the truck" : "Shoulder side of the truck"}
        >
            <span className="flex items-center gap-2">
                <select
                    value={lanes}
                    disabled={!enabled}
                    onChange={(e) => set(side === "left" ? "leftPassLanes" : "rightPassLanes", Number(e.target.value))}
                    aria-label={`${side} pass width in lanes`}
                    className="min-w-[104px] rounded-[10px] border border-[var(--line-2)] bg-[var(--surface-2)] px-3 py-2 text-[13.5px] disabled:opacity-40"
                >
                    {Array.from({ length: MAX_SIDE_PASS_LANES }, (_, i) => i + MIN_SIDE_PASS_LANES).map((n) => (
                        <option key={n} value={n}>
                            {n} lane{n === 1 ? "" : "s"}
                        </option>
                    ))}
                </select>
                <Toggle
                    on={enabled}
                    onChange={(v) => set(side === "left" ? "leftPassEnabled" : "rightPassEnabled", v)}
                    label={`${side}-side pass`}
                />
            </span>
        </FRow>
    );

    return (
        <>
            <InheritedSection
                title="Alerts & Relays"
                inherited={inherited}
                onOverride={() => setOverridden(true)}
                onResetToDefault={
                    fleet && !inherited
                        ? () =>
                              commit(
                                  fleet,
                                  `${device.name} is back on the fleet default`,
                                  `Resetting ${device.name} to the fleet default`,
                              )
                        : undefined
                }
                differences={differences}
                needsSetup={
                    unprotected
                        ? `${device.name} has no impending-collision alert, so nothing fires when a vehicle bears down on it. Resetting to the fleet default creates its protected zone and alerts.`
                        : speedingRepair
                          ? `${device.name}'s speeding alert has its limit stored as a floor, so the unit reports speeding on vehicles slower than ${draft?.speedingMph ?? "the limit"} mph instead of faster. Save these alerts (or reset to the fleet default) to move the limit where the unit reads it.`
                          : firingRepair
                            ? `${device.name}'s alerts were set up without the target types they watch, or with relay entries the unit never evaluates, so nothing fires. Save these alerts (or reset to the fleet default) to repair them.`
                            : undefined
                }
            >
                <Card>
                    <CardHead title="Impending collision" note="the reason the unit is on the truck">
                        <Tag tone="accent">Always on</Tag>
                    </CardHead>
                    <div className="mt-2">
                        <FRow title="Minimum approach speed" sub="Below this, no alert fires">
                            <span className="flex items-center gap-2">
                                <TextInput
                                    value={draft.approachMph}
                                    onChange={(e) => set("approachMph", e.target.value)}
                                    inputMode="decimal"
                                    className="w-[92px] text-right"
                                    aria-label="Minimum approach speed in mph"
                                />
                                <span className="text-[13px] text-[var(--muted)]">mph</span>
                            </span>
                        </FRow>
                        <FRow title="Driver reaction time" sub="Lead time added before predicted impact">
                            <span className="flex items-center gap-2">
                                <TextInput
                                    value={draft.reactionSeconds}
                                    onChange={(e) => set("reactionSeconds", e.target.value)}
                                    inputMode="decimal"
                                    className="w-[92px] text-right"
                                    aria-label="Driver reaction time in seconds"
                                />
                                <span className="text-[13px] text-[var(--muted)]">s</span>
                            </span>
                        </FRow>
                        <FRow stack title="Sensitivity" sub="How long an approaching vehicle is tracked before the alert fires">
                            <Slider
                                options={SENSITIVITY_STOPS}
                                value={String(draft.sensitivity)}
                                onChange={(v) => set("sensitivity", Number(v) as CollisionSensitivity)}
                                lowLabel="Longer confirmation"
                                highLabel="Earlier warning"
                            />
                        </FRow>
                    </div>
                    <CardNote>
                        Collision protection can never be switched off — not from here, and not from the cab. Sensitivity
                        sets how many consecutive collision-course detections the unit needs before firing — Highest fires
                        on one, Lowest waits for five. Older firmware ignores it and behaves as Medium.
                    </CardNote>
                </Card>

                <Card>
                    <CardHead title="Relays" note="both evaluate on impending collision" />
                    <div className="mt-2">
                        {/* Ordered by terminal number, so the rows read the way the wiring is labelled. */}
                        <FRow title={`Relay ${wiring.horn.number} · ${wiring.horn.name}`} sub={`How long the ${wiring.horn.name.toLowerCase()} is held closed`}>
                            <span className="flex items-center gap-2">
                                <TextInput
                                    value={draft.hornSeconds}
                                    onChange={(e) => set("hornSeconds", e.target.value)}
                                    inputMode="decimal"
                                    className="w-[92px] text-right"
                                    aria-label="Horn relay pulse seconds"
                                />
                                <span className="text-[13px] text-[var(--muted)]">s</span>
                            </span>
                        </FRow>
                        <FRow title={`Relay ${wiring.lights.number} · ${wiring.lights.name}`} sub={`How long the ${wiring.lights.name.toLowerCase()} are held closed`}>
                            <span className="flex items-center gap-2">
                                <TextInput
                                    value={draft.lightsSeconds}
                                    onChange={(e) => set("lightsSeconds", e.target.value)}
                                    inputMode="decimal"
                                    className="w-[92px] text-right"
                                    aria-label="Lights relay pulse seconds"
                                />
                                <span className="text-[13px] text-[var(--muted)]">s</span>
                            </span>
                        </FRow>
                    </div>
                    <CardNote>Drivers test both from the cab before rolling out.</CardNote>
                </Card>

                <Card>
                    <CardHead title="Additional alerts" note="what the unit returns to after every restart" />
                    <div className="mt-2">
                        <FRow title="Speeding" sub="Vehicle passes over the threshold">
                            <span className="flex items-center gap-2">
                                <TextInput
                                    value={draft.speedingMph}
                                    onChange={(e) => set("speedingMph", e.target.value)}
                                    inputMode="decimal"
                                    disabled={!draft.speedingEnabled}
                                    className="w-[76px] text-right"
                                    aria-label="Speeding threshold in mph"
                                />
                                <span className="text-[13px] text-[var(--muted)]">mph</span>
                                <Toggle
                                    on={draft.speedingEnabled}
                                    onChange={(v) => set("speedingEnabled", v)}
                                    label="Speeding alert"
                                />
                            </span>
                        </FRow>
                        <FRow title="Side-pass minimum speed" sub="Below this, a passing vehicle raises no alert">
                            <span className="flex items-center gap-2">
                                <TextInput
                                    value={draft.sidePassMph}
                                    onChange={(e) => set("sidePassMph", e.target.value)}
                                    inputMode="decimal"
                                    className="w-[76px] text-right"
                                    aria-label="Side pass minimum speed in mph"
                                />
                                <span className="text-[13px] text-[var(--muted)]">mph</span>
                            </span>
                        </FRow>
                        {laneRow("left", draft.leftPassEnabled, draft.leftPassLanes)}
                        {laneRow("right", draft.rightPassEnabled, draft.rightPassLanes)}
                    </div>
                    <CardNote>
                        These switches set whether each alert <b className="font-semibold text-[var(--text)]">fires at all</b>;
                        who gets emailed when one fires is the <b className="font-semibold text-[var(--text)]">Notifications</b>{" "}
                        tab's business, so an alert can be on here with its email off there. A side-pass zone is the protected
                        zone's footprint moved to that side and widened to the lanes chosen, so both watch the same stretch of
                        road. Enabling one creates its zone and trigger if the unit never had them. Drivers may toggle these for
                        a shift; a restart returns the unit to what is set here.
                    </CardNote>
                </Card>

                {!device.facilityId && (
                    <Note tone="crit">This unit has no site, so its zones and triggers cannot be edited here.</Note>
                )}
            </InheritedSection>

            <ProgressDialog run={progressRun} onClose={endProgress} />

            <SaveBar
                show={dirty && !inherited}
                busy={busy}
                canSave={valid}
                label={
                    edited
                        ? `Unsaved · ${device.name}`
                        : speedingRepair
                          ? `Speeding limit needs repair · ${device.name}`
                          : `Alerts need repair · ${device.name}`
                }
                onSave={() => commit(fromDraft(draft), `Alerts saved to ${device.name}`, `Saving ${device.name}'s alerts`)}
                onDiscard={() => setDraft(toDraft(saved))}
            />
        </>
    );
};

